package org.gymly.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.launch
import org.gymly.data.AdminRepo
import org.gymly.model.AdminExchangeRateSettings
import org.gymly.model.AdminExchangeRateSettingsUpsert
import org.gymly.util.todayIsoDate
import org.gymly.model.AdminExchangeRateStatus
import org.gymly.model.AdminSettingsModules
import org.gymly.model.AdminSettingsModulesUpsert
import org.gymly.theme.AdminColors
import org.gymly.ui.components.AdminDateField
import org.gymly.ui.components.VerticalScrollColumn
import org.gymly.util.AdminDateOutputFormat
import org.gymly.util.parseFlexibleDateToIso
import org.gymly.util.userMessage

@Composable
fun SettingsExchangePanel(
    compact: Boolean,
    modules: AdminSettingsModules = AdminSettingsModules(accessEnabled = true, exchangeRatesEnabled = true),
    onModulesChange: (AdminSettingsModules) -> Unit = {},
) {
    var loading by remember { mutableStateOf(true) }
    var saving by remember { mutableStateOf(false) }
    var savingModule by remember { mutableStateOf(false) }
    var refreshing by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var message by remember { mutableStateOf<String?>(null) }
    var settings by remember { mutableStateOf<AdminExchangeRateSettings?>(null) }
    var manualEnabled by remember { mutableStateOf(false) }
    var manualEur by remember { mutableStateOf("") }
    var manualUsd by remember { mutableStateOf("") }
    var manualDate by remember { mutableStateOf("") }
    var divisasUsesEur by remember { mutableStateOf(true) }
    val scope = rememberCoroutineScope()

    fun applySettings(data: AdminExchangeRateSettings) {
        settings = data
        manualEnabled = data.manualOverrideEnabled
        manualEur = data.manualEurRate?.toString().orEmpty()
        manualUsd = data.manualUsdRate?.toString().orEmpty()
        manualDate = data.manualEffectiveDate.orEmpty()
        divisasUsesEur = data.divisasUsesEurRate
    }

    LaunchedEffect(modules.exchangeRatesEnabled) {
        if (!modules.exchangeRatesEnabled) {
            loading = false
            return@LaunchedEffect
        }
        loading = true
        error = null
        try {
            applySettings(AdminRepo.api.exchangeRateSettings())
        } catch (e: Throwable) {
            error = e.userMessage("No se pudo cargar la cotización.")
        } finally {
            loading = false
        }
    }

    if (loading && modules.exchangeRatesEnabled) {
        Column(Modifier.fillMaxSize(), horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.Center) {
            CircularProgressIndicator(color = AdminColors.Primary)
        }
        return
    }

    val quote = settings?.current
    VerticalScrollColumn(
        Modifier.fillMaxSize(),
    ) {
        Text("Cotización BCV", fontSize = 18.sp, fontWeight = FontWeight.Bold, color = AdminColors.Text)
        Text(
            "Tipo de cambio de referencia para divisas y reportes. Se actualiza desde el BCV con respaldo automático.",
            color = AdminColors.TextMuted,
            fontSize = 13.sp,
            modifier = Modifier.padding(top = 4.dp, bottom = 16.dp),
        )
        error?.let { Text(it, color = AdminColors.Error, fontSize = 13.sp, modifier = Modifier.padding(bottom = 8.dp)) }
        message?.let { Text(it, color = AdminColors.Primary, fontSize = 13.sp, modifier = Modifier.padding(bottom = 8.dp)) }

        SettingsModuleEnableCard(
            title = "Mostrar en el panel",
            description = "Muestra u oculta el widget de tasas BCV en el panel web. No cambia los cálculos.",
            enabledLabel = "Mostrar cotización en el panel",
            checked = modules.exchangePanelVisible,
            saving = savingModule,
            onCheckedChange = { checked ->
                scope.launch {
                    savingModule = true
                    error = null
                    try {
                        onModulesChange(
                            AdminRepo.api.updateSettingsModules(
                                AdminSettingsModulesUpsert(
                                    accessEnabled = modules.accessEnabled,
                                    exchangeRatesEnabled = modules.exchangeRatesEnabled,
                                    suppressAccessWhileCapturing = modules.suppressAccessWhileCapturing,
                                    clientAccountsEnabled = modules.clientAccountsEnabled,
                                    schedulesAutomatic = modules.schedulesAutomatic,
                                    exchangePanelVisible = checked,
                                ),
                            ),
                        )
                        message = if (checked) "Widget de cotización visible en el panel." else "Widget de cotización oculto en el panel."
                    } catch (e: Throwable) {
                        error = e.userMessage("No se pudo actualizar la visibilidad de la cotización.")
                    } finally {
                        savingModule = false
                    }
                }
            },
            modifier = Modifier.padding(bottom = 16.dp),
        )

        SettingsModuleEnableCard(
            title = "Usar tasa en divisas",
            description = "Usa la tasa BCV para convertir divisas en cobros y reportes. Apagado, no se convierte nada con tasa y los cobros en \$ se registran a mano.",
            enabledLabel = "Usar tasa del BCV en divisas",
            checked = modules.exchangeRatesEnabled,
            saving = savingModule,
            onCheckedChange = { checked ->
                scope.launch {
                    savingModule = true
                    error = null
                    try {
                        onModulesChange(
                            AdminRepo.api.updateSettingsModules(
                                AdminSettingsModulesUpsert(
                                    accessEnabled = modules.accessEnabled,
                                    exchangeRatesEnabled = checked,
                                    suppressAccessWhileCapturing = modules.suppressAccessWhileCapturing,
                                    clientAccountsEnabled = modules.clientAccountsEnabled,
                                    schedulesAutomatic = modules.schedulesAutomatic,
                                ),
                            ),
                        )
                        message = if (checked) "Uso de la tasa en divisas activado." else "Uso de la tasa en divisas desactivado."
                    } catch (e: Throwable) {
                        error = e.userMessage("No se pudo actualizar el módulo de cotización.")
                    } finally {
                        savingModule = false
                    }
                }
            },
            modifier = Modifier.padding(bottom = 16.dp),
        )

        // Con el cálculo apagado no hay cotización que mostrar ni actualizar, pero la configuración sigue visible para guardar.
        if (modules.exchangeRatesEnabled) {
            ExchangeRateCard(quote, compact = compact)

            Spacer(Modifier.height(16.dp))
            Row(horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                OutlinedButton(
                    onClick = {
                        scope.launch {
                            refreshing = true
                            error = null
                            message = null
                            try {
                                AdminRepo.api.refreshExchangeRates()
                                applySettings(AdminRepo.api.exchangeRateSettings())
                                message = "Cotización actualizada."
                            } catch (e: Throwable) {
                                error = e.userMessage("No se pudo actualizar la cotización.")
                            } finally {
                                refreshing = false
                            }
                        }
                    },
                    enabled = !refreshing && !saving,
                    shape = RoundedCornerShape(10.dp),
                ) {
                    Text(if (refreshing) "Actualizando…" else "Actualizar ahora")
                }
            }
        }

        Spacer(Modifier.height(20.dp))
        Column(
            Modifier
                .fillMaxWidth()
                .clip(RoundedCornerShape(14.dp))
                .background(AdminColors.SurfaceContainer)
                .padding(16.dp),
        ) {
            Text("Configuración", fontWeight = FontWeight.SemiBold, color = AdminColors.Text)
            Spacer(Modifier.height(10.dp))
            Row(verticalAlignment = Alignment.CenterVertically) {
                Checkbox(
                    checked = manualEnabled,
                    onCheckedChange = { manualEnabled = it },
                    colors = CheckboxDefaults.colors(checkedColor = AdminColors.Primary),
                )
                Text("Usar tasa manual (cuando el BCV no responde)", color = AdminColors.Text, fontSize = 13.sp)
            }
            Row(verticalAlignment = Alignment.CenterVertically) {
                Checkbox(
                    checked = divisasUsesEur,
                    onCheckedChange = { divisasUsesEur = it },
                    colors = CheckboxDefaults.colors(checkedColor = AdminColors.Primary),
                )
                Text("Divisas: convertir USD usando tasa EUR del BCV", color = AdminColors.Text, fontSize = 13.sp)
            }
            if (manualEnabled) {
                Spacer(Modifier.height(10.dp))
                Text(
                    "Registra la tasa del día. Se agrega al historial y es la que se usa cuando el BCV no responde.",
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                )
                Spacer(Modifier.height(8.dp))
                AdminDateField(
                    label = "Fecha valor",
                    value = manualDate,
                    onValueChange = { manualDate = it },
                    placeholder = "AAAA-MM-DD o DD/MM/AAAA",
                    outputFormat = AdminDateOutputFormat.ISO,
                )
                Spacer(Modifier.height(8.dp))
                RateField("Tasa EUR (Bs/EUR)", manualEur) { manualEur = it }
                Spacer(Modifier.height(8.dp))
                RateField("Tasa USD (Bs/USD)", manualUsd) { manualUsd = it }
                Spacer(Modifier.height(10.dp))
                Button(
                    onClick = {
                        scope.launch {
                            val eur = manualEur.toDoubleOrNull()
                            val usd = manualUsd.toDoubleOrNull()
                            if (eur == null || usd == null || eur <= 0 || usd <= 0) {
                                error = "Indica tasas EUR y USD válidas."
                                return@launch
                            }
                            saving = true
                            error = null
                            message = null
                            try {
                                val date = parseFlexibleDateToIso(manualDate) ?: manualDate.ifBlank { todayIsoDate() }
                                applySettings(AdminRepo.api.registerManualExchangeRate(date, eur, usd))
                                message = "Tasa del día registrada."
                            } catch (e: Throwable) {
                                error = e.userMessage("No se pudo registrar la tasa del día.")
                            } finally {
                                saving = false
                            }
                        }
                    },
                    enabled = !saving,
                ) {
                    Text("Registrar tasa del día")
                }
            }
            Spacer(Modifier.height(14.dp))
            Button(
                onClick = {
                    scope.launch {
                        saving = true
                        error = null
                        message = null
                        try {
                            val updated = AdminRepo.api.updateExchangeRateSettings(
                                AdminExchangeRateSettingsUpsert(
                                    manualOverrideEnabled = manualEnabled,
                                    manualEurRate = null,
                                    manualUsdRate = null,
                                    manualCnyRate = null,
                                    manualTryRate = null,
                                    manualRubRate = null,
                                    manualEffectiveDate = null,
                                    divisasUsesEurRate = divisasUsesEur,
                                ),
                            )
                            applySettings(updated)
                            message = "Configuración guardada."
                        } catch (e: Throwable) {
                            error = e.userMessage("No se pudo guardar la configuración.")
                        } finally {
                            saving = false
                        }
                    }
                },
                enabled = !saving,
                shape = RoundedCornerShape(10.dp),
                colors = ButtonDefaults.buttonColors(containerColor = AdminColors.Primary, contentColor = AdminColors.OnBrand),
            ) {
                Text(if (saving) "Guardando…" else "Guardar configuración")
            }
        }

        settings?.history?.takeIf { it.isNotEmpty() }?.let { history ->
            Spacer(Modifier.height(20.dp))
            Text("Historial reciente", fontWeight = FontWeight.SemiBold, color = AdminColors.Text)
            Spacer(Modifier.height(8.dp))
            history.forEach { item ->
                Text(
                    "${item.effectiveDate} · EUR ${formatExchangeRate(item.eurRate)} · USD ${formatExchangeRate(item.usdRate)} · ${item.source}",
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                    modifier = Modifier.padding(vertical = 4.dp),
                )
            }
        }
    }
}

@Composable
fun ExchangeRateCard(quote: org.gymly.model.AdminExchangeRateQuote?, compact: Boolean, modifier: Modifier = Modifier) {
    Column(
        modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(16.dp))
            .background(AdminColors.Primary)
            .padding(18.dp),
    ) {
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.Top) {
            Column {
                Text("Tipo de cambio BCV", color = AdminColors.OnBrand.copy(alpha = 0.9f), fontSize = 12.sp)
                Text(
                    quote?.effectiveDate?.let { "Fecha valor: $it" } ?: "Sin fecha valor",
                    color = AdminColors.OnBrand,
                    fontWeight = FontWeight.Bold,
                    fontSize = if (compact) 14.sp else 16.sp,
                    modifier = Modifier.padding(top = 4.dp),
                )
            }
            val status = quote?.status ?: AdminExchangeRateStatus.UNAVAILABLE
            Text(
                status.labelEs(),
                color = AdminColors.OnBrand,
                fontSize = 12.sp,
                fontWeight = FontWeight.SemiBold,
                modifier = Modifier
                    .clip(RoundedCornerShape(8.dp))
                    .background(AdminColors.OnBrand.copy(alpha = if (status == AdminExchangeRateStatus.OK) 0.2f else 0.35f))
                    .padding(horizontal = 8.dp, vertical = 4.dp),
            )
        }
        Spacer(Modifier.height(14.dp))
        ExchangeRateRow("€", "EUR", quote?.eurRate)
        Spacer(Modifier.height(8.dp))
        ExchangeRateRow("$", "USD", quote?.usdRate)
        if (!compact) {
            quote?.cnyRate?.let {
                Spacer(Modifier.height(8.dp))
                ExchangeRateRow("¥", "CNY", it)
            }
            quote?.tryRate?.let {
                Spacer(Modifier.height(8.dp))
                ExchangeRateRow("₺", "TRY", it)
            }
            quote?.rubRate?.let {
                Spacer(Modifier.height(8.dp))
                ExchangeRateRow("₽", "RUB", it)
            }
        }
        quote?.source?.let { source ->
            Spacer(Modifier.height(10.dp))
            Text("Fuente: $source", color = AdminColors.OnBrand.copy(alpha = 0.8f), fontSize = 12.sp)
        }
    }
}

@Composable
private fun ExchangeRateRow(symbol: String, code: String, rate: Double?) {
    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
        Text("$symbol  Bs / $code", color = AdminColors.OnBrand.copy(alpha = 0.9f), fontSize = 13.sp)
        Text(
            formatExchangeRate(rate),
            color = AdminColors.OnBrand,
            fontWeight = FontWeight.Bold,
            fontSize = 18.sp,
        )
    }
}

@Composable
private fun RateField(label: String, value: String, onChange: (String) -> Unit) {
    Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
    Spacer(Modifier.height(4.dp))
    OutlinedTextField(
        value = value,
        onValueChange = onChange,
        modifier = Modifier.fillMaxWidth(),
        singleLine = true,
        shape = RoundedCornerShape(10.dp),
        colors = OutlinedTextFieldDefaults.colors(
            focusedBorderColor = AdminColors.Primary,
            unfocusedBorderColor = AdminColors.Outline,
        ),
    )
}
